import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import '../../App.css';
import MultiSelect, { MultiSelectOption, SelectId } from '../MultiSelect';
import AssignmentField from './AssignmentField';
import type { AssignmentPerson } from '../AssignmentDrawer';
import ToggleSwitch from './ToggleSwitch';
import ActivityPicker from './ActivityPicker';
import CoursePicker from './CoursePicker';
import FormField from '../ui/FormField';
import SearchableSelect from '../SearchableSelect';
import SchemaForm from '../../schemaForm/SchemaForm';
import FirstOccurrenceField from '../../schemaForm/fields/FirstOccurrenceField';
import TimePickerField from '../../schemaForm/fields/TimePickerField';
import RecurringPatternField from '../../schemaForm/fields/RecurringPatternField';
import DefaultAttendanceField from '../../schemaForm/fields/DefaultAttendanceField';
import type { SchemaFieldProps, SchemaFormSection } from '../../schemaForm/types';
import { checkPermission } from '../../utils/permissionUtils';

interface ISelectOption {
  _id: string | number;
  name: string;
  role?: { type?: string; name?: string; permissions?: Record<string, number> };
  /**
   * Member option only. `GET /api/members` returns each member's own groups;
   * the assignment field uses them to show whether someone is on the roster
   * directly or via an assigned group.
   */
  groups?: Array<{ _id: string | number; name: string }>;
}

export interface IEventTemplateFormData {
  /** Admin users notified about this template's events (migration 0047). */
  followers?: SelectId[];
  name: string;
  description: string;
  location?: string;
  firstOccurrence: string;
  endsOn: string;
  startTime: string;
  endTime: string;
  recurringPattern: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  defaultAttendance: boolean | null;
  members: SelectId[];
  groups: SelectId[];
  coveredByMembershipIds: SelectId[];
  responsible: string | null;
  activityId: string | null;
  courseId: number | null;
  isActive?: boolean;
}

interface IEventTemplateFormProps {
  initialData?: Partial<IEventTemplateFormData>;
  onSubmit: (data: IEventTemplateFormData) => Promise<void>;
  loading?: boolean;
  submitButtonText: string;
  /** Show isActive checkbox (only for edit mode) */
  showIsActive?: boolean;
  /** Optional pre-fetched members to avoid redundant API calls */
  prefetchedMembers?: ISelectOption[];
  /** Optional pre-fetched groups to avoid redundant API calls */
  prefetchedGroups?: ISelectOption[];
  /** Optional pre-fetched admins to avoid redundant API calls */
  prefetchedAdmins?: ISelectOption[];
  /** Scoped admin's allowed activity ids (from useAuth/accessScope). */
  allowedActivityIds?: number[];
  /** Optional children to render before submit button (e.g., change preview) */
  children?: React.ReactNode;
  /** Callback when form values change (for change detection in edit mode) */
  onValuesChange?: (values: IEventTemplateFormData) => void;
  /** Create mode: say that a course-linked template starts with the season's enrollees (D13). */
  seasonSeedHint?: boolean;
  /** Saved roster by name, incl. people outside the viewer's scope (D18). */
  rosterPeople?: AssignmentPerson[];
}

/** Today as YYYY-MM-DD (matches the legacy default first-occurrence). */
function todayDateStr(): string {
  return new Date().toISOString().split('T')[0] || '';
}

/** Normalize an ISO/date string to YYYY-MM-DD (legacy firstOccurrence handling). */
function toDateInput(value?: string): string {
  if (!value) return '';
  return value.includes('T') ? (new Date(value).toISOString().split('T')[0] || '') : value;
}

/**
 * Schema-driven event template form. Scalar fields (name, description, location,
 * isActive) are generated from the `eventTemplate` entity meta. Date/time and
 * tri-state controls keep their bespoke UX via SchemaForm overrides
 * (firstOccurrence, startTime/endTime, recurringPattern, defaultAttendance).
 * The activity/course/responsible pickers are FK fields rendered with overrides
 * (they reuse the existing scoped pickers). The m2m assignment pickers
 * (members, groups, coveredByMembershipIds) are NOT part of the entity meta, so
 * they remain wrapper-owned local state rendered as inline section nodes,
 * exactly as the legacy form did. The payload shape is preserved verbatim.
 */
const EventTemplateForm: React.FC<IEventTemplateFormProps> = ({
  initialData,
  onSubmit,
  loading = false,
  submitButtonText,
  showIsActive = false,
  prefetchedMembers,
  prefetchedGroups,
  prefetchedAdmins,
  allowedActivityIds,
  children,
  onValuesChange,
  seasonSeedHint = false,
  rosterPeople,
}) => {
  const { t } = useTranslation();

  // m2m assignments + their option lists are not part of the entity meta, so the
  // wrapper owns them (mirrors the legacy local state).
  const [members, setMembers] = useState<SelectId[]>([]);
  const [groups, setGroups] = useState<SelectId[]>([]);
  // Labels as last seeded (= saved); new ones get applied by the server on save.
  const [savedGroups, setSavedGroups] = useState<SelectId[]>([]);
  const [coveredByMembershipIds, setCoveredByMembershipIds] = useState<SelectId[]>([]);
  const [followers, setFollowers] = useState<SelectId[]>([]);
  const [showAllMemberships, setShowAllMemberships] = useState(false);

  const [allMembers, setAllMembers] = useState<ISelectOption[]>([]);
  const [allGroups, setAllGroups] = useState<ISelectOption[]>([]);
  const [admins, setAdmins] = useState<ISelectOption[]>([]);
  const [allMemberships, setAllMemberships] = useState<Array<{ _id: number; name: string; activities?: Array<{ _id: number }> }>>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // SchemaForm-owned scalar values, mirrored here so onValuesChange can emit the
  // full legacy payload (meta scalars + wrapper-owned m2m selections) and so the
  // coverage picker can read the currently selected activity.
  const [schemaValues, setSchemaValues] = useState<Record<string, any>>({});

  const hasPrefetchedOptions = prefetchedMembers && prefetchedGroups && prefetchedAdmins;

  // Fetch members, groups, admins (and memberships) for the assignment pickers.
  useEffect(() => {
    if (hasPrefetchedOptions) {
      setAllMembers(prefetchedMembers);
      setAllGroups(prefetchedGroups);
      setAdmins(prefetchedAdmins);
      setOptionsLoading(false);
      return;
    }

    const fetchSelectOptions = async () => {
      try {
        const [membersRes, groupsRes, usersRes, membershipsRes] = await Promise.all([
          api.members.list(),
          api.groups.list(),
          api.users.list(),
          api.memberships.list(),
        ]);

        setAllMembers(membersRes.data as ISelectOption[]);
        setAllGroups(groupsRes.data as ISelectOption[]);
        setAllMemberships(membershipsRes.data as Array<{ _id: number; name: string; activities?: Array<{ _id: number }> }>);
        setAdmins((usersRes.data as ISelectOption[]).filter(u =>
          u.role?.type === 'admin' ||
          u.role?.name === 'Owner' ||
          checkPermission(u.role?.permissions, 'events:write')
        ));
        setOptionsLoading(false);
      } catch {
        setOptionsLoading(false);
      }
    };
    fetchSelectOptions();
  }, [hasPrefetchedOptions, prefetchedMembers, prefetchedGroups, prefetchedAdmins]);

  // Seed the wrapper-owned m2m selections from initialData.
  useEffect(() => {
    if (initialData) {
      setMembers(initialData.members || []);
      setGroups(initialData.groups || []);
      setSavedGroups(initialData.groups || []);
      setCoveredByMembershipIds(initialData.coveredByMembershipIds || []);
      setFollowers(initialData.followers || []);
    }
  }, [initialData]);

  // Seed the SchemaForm scalar values. activityId/courseId/responsibleId use the
  // meta field names; the legacy payload keys (activityId/courseId/responsible)
  // are mapped on the way out.
  const initialValues = useMemo(() => ({
    name: initialData?.name ?? '',
    description: initialData?.description ?? '',
    location: initialData?.location ?? '',
    firstOccurrence: initialData?.firstOccurrence ? toDateInput(initialData.firstOccurrence) : todayDateStr(),
    endsOn: initialData?.endsOn ? toDateInput(initialData.endsOn) : '',
    startTime: initialData?.startTime ?? '09:00',
    endTime: initialData?.endTime ?? '10:00',
    recurringPattern: initialData?.recurringPattern ?? 'weekly',
    defaultAttendance: initialData?.defaultAttendance ?? null,
    responsibleId: initialData?.responsible != null && initialData.responsible !== '' ? Number(initialData.responsible) : null,
    activityId: initialData?.activityId != null && initialData.activityId !== '' ? Number(initialData.activityId) : null,
    courseId: initialData?.courseId ?? null,
    isActive: initialData?.isActive ?? true,
  }), [initialData]);

  // Build the full legacy payload from the SchemaForm scalar values + the
  // wrapper-owned m2m selections.
  const buildPayload = (values: Record<string, any>): IEventTemplateFormData => ({
    name: values.name ?? '',
    description: values.description ?? '',
    location: values.location || undefined,
    firstOccurrence: values.firstOccurrence ?? '',
    endsOn: values.endsOn ?? '',
    startTime: values.startTime ?? '',
    endTime: values.endTime ?? '',
    recurringPattern: values.recurringPattern ?? 'weekly',
    defaultAttendance: values.defaultAttendance ?? null,
    members,
    groups,
    coveredByMembershipIds,
    followers,
    responsible: values.responsibleId != null && values.responsibleId !== '' ? String(values.responsibleId) : null,
    activityId: values.activityId != null && values.activityId !== '' ? String(values.activityId) : null,
    courseId: values.courseId ?? null,
    isActive: values.isActive ?? true,
  });

  // Emit the combined payload to the parent whenever scalars or m2m change.
  useEffect(() => {
    if (onValuesChange && !optionsLoading) {
      onValuesChange(buildPayload(schemaValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schemaValues, members, groups, coveredByMembershipIds, followers, optionsLoading]);

  const handleSubmit = (values: Record<string, any>) => {
    return onSubmit(buildPayload(values));
  };

  // Override for the activity picker (scoped + required-when-scoped, help text).
  const ActivityField = useMemo(
    () =>
      ({ value, onChange }: SchemaFieldProps) => (
        <ActivityPicker
          value={value == null || value === '' ? null : Number(value)}
          onChange={(v) => onChange(v)}
          required={!!(allowedActivityIds && allowedActivityIds.length > 0)}
          allowedActivityIds={allowedActivityIds}
          label={t('templates.activity')}
          helpText={t('templates.activityHelp')}
        />
      ),
    [allowedActivityIds, t]
  );

  // Override for the course picker (filtered by the currently selected activity).
  const CourseField = useMemo(
    () =>
      ({ value, onChange, values }: SchemaFieldProps) => (
        <CoursePicker
          value={value ?? null}
          activityId={values?.activityId == null || values?.activityId === '' ? null : Number(values.activityId)}
          onChange={(v) => onChange(v)}
          label={t('templates.course')}
        />
      ),
    [t]
  );

  // Override for the optional `endsOn` date. Uses the same native date UX as
  // firstOccurrence, with a hint that course-linked templates also stop at the
  // course term. Rendered as an override (not the auto-derived default) so it
  // reuses the existing `templates.*` i18n keys and can carry the help text.
  const EndsOnField = useMemo(
    () =>
      ({ value, onChange, error }: SchemaFieldProps) => (
        <FormField htmlFor="sf-endsOn" label={t('templates.endsOn')} error={error}>
          <input
            type="date"
            id="sf-endsOn"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
          />
          <small className="form-help-text">{t('templates.endsOnHelp')}</small>
        </FormField>
      ),
    [t]
  );

  // Override for the responsible person select (admins with events:write only).
  const ResponsibleField = useMemo(
    () =>
      ({ value, onChange }: SchemaFieldProps) => (
        <FormField htmlFor="sf-responsibleId" label={t('events.responsible')}>
          <SearchableSelect
            id="sf-responsibleId"
            options={admins.map((a) => ({ _id: a._id, name: a.name }))}
            value={value ?? null}
            onChange={(v) => onChange(v == null ? null : Number(v))}
            placeholder={t('common.select')}
          />
        </FormField>
      ),
    [admins, t]
  );

  // One roster control; picking a group copies its members and keeps it as a label.
  const showSeasonSeedHint = seasonSeedHint && schemaValues.courseId != null && schemaValues.courseId !== '';
  const assignmentField = (
    <React.Fragment key="assignment">
      <AssignmentField
        id="assignment"
        label={t('events.assignMembers')}
        drawerTitle={
          schemaValues.name
            ? `${t('events.assignMembers')} — ${schemaValues.name}`
            : t('events.assignMembers')
        }
        people={allMembers}
        rosterPeople={rosterPeople}
        groups={allGroups}
        memberIds={members}
        groupIds={groups}
        savedGroupIds={savedGroups}
        onChange={(next) => {
          setMembers(next.memberIds);
          setGroups(next.groupIds);
        }}
      />
      {showSeasonSeedHint && (
        <small className="form-help-text" data-testid="season-seed-hint">
          {t('templates.seasonEnrolleesHint')}
        </small>
      )}
    </React.Fragment>
  );

  /**
   * Followers — admin users who get notified about this template's events
   * (absences, capacity, comments) without being its responsible user.
   *
   * Stricter than the `admins` list used by the responsible picker, which also
   * admits anyone holding `events:write`. The backend only accepts admin/owner
   * roles, because `getEventWatchers` filters to those — admin-audience
   * notification rows are hidden from non-admins in the preferences matrix, so
   * anyone else would be a follower who never receives anything.
   */
  const followerOptions = admins.filter(
    (u) => u.role?.type === 'admin' || u.role?.name === 'Owner',
  ) as MultiSelectOption[];

  const followersField = (
    <MultiSelect
      key="followers"
      id="followers"
      label={t('events.followers')}
      labelHelpText={t('events.followersHelp')}
      options={followerOptions}
      selectedIds={followers}
      onChange={setFollowers}
      placeholder={t('events.selectFollowers')}
    />
  );

  // Coverage memberships picker — defaults to memberships linked to the
  // template's activity (via membership_activities), with a "Show all" toggle.
  const coveredByField = (() => {
    const rawActivityId = schemaValues.activityId;
    const numericActivityId = rawActivityId == null || rawActivityId === '' ? null : Number(rawActivityId);
    const activityFilter = !showAllMemberships && numericActivityId != null;
    const filtered = activityFilter
      ? allMemberships.filter((m) => (m.activities || []).some((a) => a._id === numericActivityId))
      : allMemberships;
    const options: MultiSelectOption[] = filtered.map((m) => ({ _id: m._id, name: m.name }));
    for (const sid of coveredByMembershipIds) {
      if (!options.some((o) => String(o._id) === String(sid))) {
        const found = allMemberships.find((m) => String(m._id) === String(sid));
        if (found) options.push({ _id: found._id, name: found.name });
      }
    }
    return (
      <React.Fragment key="coveredBy">
        <MultiSelect
          id="coveredBy"
          label={t('events.coveredBy')}
          labelHelpText={t('events.coveredByHelp')}
          options={options}
          selectedIds={coveredByMembershipIds}
          onChange={setCoveredByMembershipIds}
          placeholder={t('events.selectCoveredBy')}
        />
        {numericActivityId != null && (
          <div className="form-group" style={{ marginTop: '-0.5rem' }}>
            <ToggleSwitch
              checked={showAllMemberships}
              onChange={setShowAllMemberships}
              label={t('events.showAllMemberships')}
            />
          </div>
        )}
      </React.Fragment>
    );
  })();

  const sections: SchemaFormSection[] = [
    {
      items: ['name', 'description', 'location', 'activityId', 'courseId', 'recurringPattern', 'firstOccurrence', 'endsOn'],
    },
    // startTime + endTime share a row (legacy form-row / form-group-half layout).
    {
      columns: 2,
      items: ['startTime', 'endTime'],
    },
    {
      items: [
        ...(showIsActive ? ['isActive'] : []),
        assignmentField,
        coveredByField,
        'defaultAttendance',
        'responsibleId',
        followersField,
      ],
    },
  ];

  if (optionsLoading) {
    return <p>{t('common.loading')}</p>;
  }

  return (
    <SchemaForm
      entity="eventTemplate"
      sections={sections}
      overrides={{
        firstOccurrence: FirstOccurrenceField,
        endsOn: EndsOnField,
        startTime: TimePickerField,
        endTime: TimePickerField,
        recurringPattern: RecurringPatternField,
        defaultAttendance: DefaultAttendanceField,
        activityId: ActivityField,
        courseId: CourseField,
        responsibleId: ResponsibleField,
      }}
      requiredOverrides={{ description: true }}
      initialValues={initialValues}
      onValuesChange={setSchemaValues}
      onSubmit={handleSubmit}
      footer={children}
      submitLabel={submitButtonText}
      loading={loading}
    />
  );
};

export default EventTemplateForm;
